// CB:BEGIN admin-quote-drafts-page
import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import AdminLayout from '../../components/admin/AdminLayout';
import SettingsCard from '../../components/ui/SettingsCard';

interface DraftQuoteRow {
  id: string;
  quoteNumber: string;
  status: string;
  homeownerName: string | null;
  propertyAddress1: string;
  propertyCity: string | null;
  propertyState: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AdminQuoteDraftsPageProps {
  onNavigate?: (screen: string) => void;
}

const AdminQuoteDraftsPage: React.FC<AdminQuoteDraftsPageProps> = ({ onNavigate }) => {
  const api = useApi();
  const [drafts, setDrafts] = useState<DraftQuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [findNumber, setFindNumber] = useState('');
  const [findLoading, setFindLoading] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);

  const handleFindByNumber = async () => {
    const num = findNumber.trim();
    if (!num) return;
    setFindError(null);
    setFindLoading(true);
    try {
      const quote = await api.get<{ id: string }>(`/api/admin/quotes?quoteNumber=${encodeURIComponent(num)}`);
      window.location.assign(`/admin/quotes/new?quoteId=${quote.id}`);
    } catch {
      setFindError(`Quote "${num}" not found`);
    } finally {
      setFindLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<DraftQuoteRow[]>('/api/admin/quotes?status=DRAFT')
      .then((list) => {
        if (!cancelled) setDrafts(list);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load drafts');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api identity unstable; fetch once on mount
  }, []);

  const handleEdit = (quoteId: string) => {
    if (typeof window !== 'undefined') {
      window.location.assign(`/admin/quotes/new?quoteId=${quoteId}`);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'short' });
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center gap-4">
        {onNavigate && (
          <button
            type="button"
            onClick={() => onNavigate('admin-dashboard')}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            ← Back to Dashboard
          </button>
        )}
        <button
          type="button"
          onClick={() => onNavigate?.('admin-quotes-new')}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 px-3 py-2 rounded-lg border border-blue-300 hover:bg-blue-50"
        >
          Create new quote
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saved draft quotes</h1>
          <p className="text-gray-600 mt-1">Edit a draft or create a new quote.</p>
        </div>
      </div>

      <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Find quote by number</span>
          <input
            type="text"
            placeholder="e.g. CB-20260129-9823"
            value={findNumber}
            onChange={(e) => setFindNumber(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFindByNumber()}
            className="ml-2 mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={handleFindByNumber}
          disabled={findLoading || !findNumber.trim()}
          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
        >
          {findLoading ? 'Finding…' : 'Open'}
        </button>
        {findError && <p className="text-sm text-red-600" role="alert">{findError}</p>}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4" role="alert">
          {error}
        </div>
      )}

      <SettingsCard title="Drafts">
        {loading ? (
          <p className="text-gray-600 py-4">Loading…</p>
        ) : drafts.length === 0 ? (
          <p className="text-gray-600 py-4">No saved drafts. Create a quote and use “Save as draft” to see drafts here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Quote #</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Homeowner</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Property</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Updated</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {drafts.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 text-sm text-gray-900">{row.quoteNumber}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{row.homeownerName || '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {row.propertyAddress1}
                      {row.propertyCity || row.propertyState ? `, ${[row.propertyCity, row.propertyState].filter(Boolean).join(', ')}` : ''}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">{formatDate(row.updatedAt)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleEdit(row.id)}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>
    </AdminLayout>
  );
};

export default AdminQuoteDraftsPage;
// CB:END admin-quote-drafts-page
